/** Cloud persistence layer - saves/loads maps via the API + S3. */

import { MindMapDocument } from '@/model/document';
import {
  createMap,
  listMaps,
  getMap,
  updateMapMeta,
  deleteMap,
  uploadMapDocument,
  downloadMapDocument,
} from '@/api/maps';
import type { MapListItem } from '@/api/types';

/**
 * Save a map to the cloud: updates metadata via API
 * and uploads Yjs state to S3 via the signed upload URL.
 */
export async function cloudSaveMap(
  mapDoc: MindMapDocument,
  mapId: string,
  title: string,
): Promise<void> {
  const { uploadUrl } = await updateMapMeta(mapId, title);
  const state = mapDoc.encodeState();
  await uploadMapDocument(uploadUrl, state);
}

/**
 * Load a map from the cloud: fetches download URL via API,
 * downloads Yjs binary from S3, and applies it to the document.
 */
export async function cloudLoadMap(
  mapId: string,
  mapDoc: MindMapDocument,
): Promise<void> {
  const detail = await getMap(mapId);
  const state = await downloadMapDocument(detail.downloadUrl);
  mapDoc.applyState(state);
}

/**
 * Create a new map in the cloud: calls the API, initializes a
 * MindMapDocument, uploads initial state to S3, and returns the result.
 */
export async function cloudCreateMap(
  title: string,
): Promise<{ mapId: string; mapDoc: MindMapDocument }> {
  const response = await createMap(title);
  const mapDoc = new MindMapDocument();
  mapDoc.initialize(title);
  const state = mapDoc.encodeState();
  await uploadMapDocument(response.uploadUrl, state);
  return { mapId: response.mapId, mapDoc };
}

/**
 * List all maps from the cloud API.
 */
export async function cloudListMaps(): Promise<MapListItem[]> {
  return listMaps();
}

/**
 * Delete a map via the cloud API.
 */
export async function cloudDeleteMap(mapId: string): Promise<void> {
  return deleteMap(mapId);
}
